import { colors, Icon, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useEffect, useRef, useState } from "react";
import { Accept, useDropzone } from "react-dropzone";

type DropzoneAcceptType = "only-images" | "only-pdf" | "images_and_pdf";

const ACCEPT_MAP: Record<DropzoneAcceptType, Accept> = {
  "only-images": {
    "image/*": [],
  },
  "only-pdf": {
    "application/pdf": [".pdf"],
  },
  images_and_pdf: {
    "image/*": [],
    "application/pdf": [".pdf"],
  },
};

interface FileWithPreview extends File {
  preview: string;
}

type DropzoneGlobalProps = {
  accept: DropzoneAcceptType;
  maxFiles: number;
  /** Callback chamado quando a lista de arquivos altera (para uso em uploads). */
  onFilesChange?: (files: File[]) => void;
};

export default function DropzoneGlobal({
  accept,
  maxFiles,
  onFilesChange,
}: DropzoneGlobalProps) {
  const [filePreview, setFilePreview] = useState<FileWithPreview[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const filePreviewRef = useRef<FileWithPreview[]>([]);
  filePreviewRef.current = filePreview;

  const isImageFile = (file: File) => file.type.startsWith("image/");

  const onFilesChangeRef = useRef(onFilesChange);
  onFilesChangeRef.current = onFilesChange;

  // Notifica o pai apenas quando a lista de arquivos muda (evita limpar estado no re-render do pai)
  useEffect(() => {
    onFilesChangeRef.current?.(files);
  }, [files]);

  // Lida com o evento de arrastar e soltar arquivos, processando os arquivos aceitos.
  const onDrop = (acceptedFiles: File[]) => {
    const filesWithPreview = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );

    setFiles(acceptedFiles);
    setFilePreview((prevFiles) => [...prevFiles, ...filesWithPreview]);
  };

  const removeFile = (fileName: string) => {
    setFilePreview((prev) => {
      const fileToRemove = prev.find((f) => f.name === fileName);
      if (fileToRemove?.preview) URL.revokeObjectURL(fileToRemove.preview);
      return prev.filter((file) => file.name !== fileName);
    });
    setFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  useEffect(() => {
    return () => {
      filePreviewRef.current.forEach((file) => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, []);

  // configuração do dropzone
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    maxFiles: maxFiles,
    accept: ACCEPT_MAP[accept],
  });

  return (
    <>
      <MDBox
        {...getRootProps()}
        sx={{
          border: "2px dashed gray",
          borderRadius: "8px",
          padding: "20px",
          textAlign: "center",
          backgroundColor: colors.grey[50],
          cursor: "pointer",
        }}
      >
        <input {...getInputProps()} />
        {isDragAccept && (
          <Typography variant="subtitle1" color="green">
            Todos os arquivos serão aceitos
          </Typography>
        )}
        {isDragReject && (
          <Typography variant="subtitle1" color="error">
            Ops! Arquivo não suportado, aceitamos apenas:{" "}
            {accept === "only-images" && "imagens"}
            {accept === "only-pdf" && "PDF"}
            {accept === "images_and_pdf" && "imagens e PDF"} ou excedeu o limite
            de arquivos.
          </Typography>
        )}
        {!isDragActive && filePreview.length === 0 && (
          <Typography variant="body2" color="Highlight">
            Arraste e solte os arquivos aqui.
          </Typography>
        )}

        {/* PREVIEW DOS ARQUIVOS NO DROPZONE */}
        <MDBox
          display="flex"
          gap={1}
          justifyContent="center"
          flexWrap="wrap"
          mt={filePreview.length > 0 ? 2 : 0}
        >
          {filePreview.length > 0 &&
            filePreview.map((file, idx) => (
              <MDBox
                key={`${file.name}-${idx}`}
                sx={{
                  width: 100,
                  minHeight: 100,
                  padding: "10px",
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: colors.cyan[50],
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {isImageFile(file) ? (
                  <img
                    src={file.preview}
                    alt={file.name}
                    style={{
                      width: "100%",
                      height: 80,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                ) : (
                  <Icon sx={{ fontSize: 40 }}>attach_file</Icon>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    width: "100%",
                    mt: 0.5,
                  }}
                >
                  {file.name}
                </Typography>
              </MDBox>
            ))}
        </MDBox>
      </MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={1}
      >
        <Typography variant="body2" color="GrayText">
          Você pode arrastar e soltar até {maxFiles} arquivos.
        </Typography>
        {filePreview.length > maxFiles && (
          <Typography variant="body2" color="red" fontWeight="bold">
            😕 Você excedeu o limite de arquivos.
          </Typography>
        )}
      </MDBox>

      {/* LISTA DE ARQUIVOS PARA UPLOADS */}
      {filePreview.length > 0 && (
        <MDBox my={2}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon color="success" fontSize="medium">
              cloud_upload_outlined
            </Icon>
            <Typography textAlign="left" color="GrayText" variant="h6">
              Arquivos para uploads:
            </Typography>
          </MDBox>
          {filePreview.map((file) => (
            <MDBox
              key={file.name}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
              sx={{ backgroundColor: colors.grey[100], padding: "10px" }}
            >
              <Typography variant="body2">{file.name}</Typography>
              <MDButton
                variant="contained"
                color="error"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(file.name);
                }}
              >
                Remover
              </MDButton>
            </MDBox>
          ))}
        </MDBox>
      )}
    </>
  );
}
